//
import type { Action } from '@/types/Components/Actions'
import type { RouterLink } from '@/types/Components/Router'
import type { Assignment, SourceFile } from '@/types/Models/Assignment'
import { notify } from '@kyvg/vue3-notification'
import { computed, type ComputedRef } from 'vue'
import { useDownloadLabelsAction } from '@/composables/Labels/useDownloadLabelsAction'
import {
  ARCHIVE_ASSIGNMENT,
  ASSIGNMENT_HISTORY,
  DOWNLOAD_DOCUMENTS,
  DOWNLOAD_LABELS,
  DOWNLOAD_REPORT,
  DOWNLOAD_SOURCE,
  INTEGRATION_ASSIGNMENT,
  INTEGRATION_ITEM,
  ITEM_HISTORY,
  PROCESS_ASSIGNMENT,
  RECREATE_LABEL,
  RECREATE_LABELS,
  REOPTIMIZE,
  STATUS_MANAGEMENT,
  SYNC_TRACKING,
  UNARCHIVE_ASSIGNMENT,
  VIEW_ASSIGNMENT,
  VIEW_REPORT,
  VIEW_SHIPMENTS,
} from '@/const/AssignmentActions'
import {
  COMPLETE,
  LABELS_CREATING,
  LABELS_RELEASED,
  OPEN,
  REPORT_ACCEPTED,
  REPORT_DENIED,
  REPORT_RELEASED,
} from '@/const/AssignmentStatuses'
import { CONSOLIDATE, RETURN, SINGLE } from '@/const/AssignmentTypes'
import {
  ASSIGNMENT_HISTORY_MODAL,
  ASSIGNMENT_INTEGRATION_MODAL,
  ASSIGNMENT_STATUS_MODAL,
  ITEM_HISTORY_MODAL,
  ITEM_INTEGRATION_MODAL,
} from '@/const/ModalTypes'
import { catchErrors } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { type AccountStore, useAccountStore } from '@/store/account'
import { type AssignmentStore, useAssignmentStore } from '@/store/assignment'
import { type AssignmentSummaryStore, useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { type ItemStore, useItemStore } from '@/store/item'
import {
  CUSTOMER_ASSIGNMENTS_OPTIMIZATION_REPORT,
  CUSTOMER_ASSIGNMENTS_DETAILS,
  CUSTOMER_SHIPMENTS,
  STAFF_ASSIGNMENTS_OPTIMIZATION_REPORT,
  STAFF_ASSIGNMENTS_DETAILS,
  STAFF_SHIPMENTS,
} from '@/types/router'
import TrackingProxy from '@/proxies/TrackingProxy'
import AssignmentProxy from '@/proxies/AssignmentProxy'
import AssignmentStatusProxy from '@/proxies/AssignmentStatusProxy'

export const ActionsConfig: Action[] = [
  {
    handle: VIEW_ASSIGNMENT,
    icon: 'fa-file',
    name: 'View assignment',
    types: [SINGLE, CONSOLIDATE, RETURN],
    order: 20,
  },
  {
    handle: PROCESS_ASSIGNMENT,
    icon: 'fa-arrow-right',
    name: 'Finish Buffering',
    types: [SINGLE, CONSOLIDATE],
    statuses: () => [OPEN],
    order: 25,
  },
  {
    handle: VIEW_REPORT,
    icon: 'fa-file',
    name: 'View report',
    types: [CONSOLIDATE],
    extraCheck: (assignment: Assignment) => !assignment.hidePrice,
    statuses: (_: Assignment) => [
      REPORT_RELEASED,
      REPORT_ACCEPTED,
      REPORT_DENIED,
      LABELS_CREATING,
      LABELS_RELEASED,
      COMPLETE,
    ],
    order: 30,
  },
  {
    handle: DOWNLOAD_SOURCE,
    icon: 'fa-download',
    name: 'Download source',
    types: [CONSOLIDATE],
    order: 50,
  },
  {
    handle: DOWNLOAD_REPORT,
    icon: 'fa-download',
    name: 'Download report',
    types: [CONSOLIDATE],
    extraCheck: (assignment: Assignment) => !assignment.hidePrice,
    statuses: (_: Assignment) => [
      REPORT_RELEASED,
      REPORT_ACCEPTED,
      REPORT_DENIED,
      LABELS_CREATING,
      LABELS_RELEASED,
      COMPLETE,
    ],
    order: 40,
  },
  {
    handle: DOWNLOAD_LABELS,
    icon: 'fa-download',
    name: (assignment: Assignment) => (assignment.type === CONSOLIDATE ? 'Download labels' : 'Download label'),
    types: [CONSOLIDATE, SINGLE, RETURN],
    statuses: () => [LABELS_RELEASED],
    order: 10,
  },
  {
    handle: DOWNLOAD_DOCUMENTS,
    icon: 'fa-download',
    name: 'Download documents',
    types: [CONSOLIDATE, SINGLE, RETURN],
    extraCheck: (assignment: Assignment) => !!assignment.documentsUrl,
    statuses: () => [LABELS_RELEASED],
    order: 15,
  },
  {
    handle: VIEW_SHIPMENTS,
    icon: 'fa-truck',
    name: (assignment: Assignment) => (assignment.type === CONSOLIDATE ? 'View shipments' : 'View shipment'),
    statuses: () => [LABELS_RELEASED, COMPLETE],
    order: 60,
  },
  {
    handle: ARCHIVE_ASSIGNMENT,
    icon: 'fa-eye-slash',
    name: 'Archive assignment',
    extraCheck: (assignment: Assignment) => !assignment.archived,
    order: 70,
  },
  {
    handle: UNARCHIVE_ASSIGNMENT,
    icon: 'fa-eye',
    name: 'Unarchive assignment',
    extraCheck: (assignment: Assignment) => assignment.archived,
    order: 80,
  },
]

export const AssignmentSettingsConfig: Action[] = [
  {
    handle: INTEGRATION_ASSIGNMENT,
    icon: 'fa-right-to-bracket',
    name: 'Integration logs',
    extraCheck: (assignment: Assignment) => assignment.source == 'integration',
    order: 10,
  },
  {
    handle: STATUS_MANAGEMENT,
    icon: 'fa-timeline',
    name: 'Status management',
    extraCheck: (assignment: Assignment, isStaff: boolean) => !assignment.archived && isStaff,
    order: 20,
  },
  {
    handle: SYNC_TRACKING,
    icon: 'fa-rotate',
    name: 'Sync tracking events',
    order: 30,
  },
  {
    handle: REOPTIMIZE,
    icon: 'fa-rotate',
    name: 'Reoptimize assignment',
    types: [SINGLE, CONSOLIDATE],
    extraCheck: (assignment: Assignment, isStaff: boolean) => !assignment.archived && isStaff,
    order: 31,
  },
  {
    handle: RECREATE_LABELS,
    icon: 'fa-rotate',
    name: 'Recreate labels',
    types: [SINGLE, CONSOLIDATE],
    extraCheck: (assignment: Assignment, isStaff: boolean) => !assignment.archived && isStaff,
    order: 32,
  },
  {
    handle: ASSIGNMENT_HISTORY,
    icon: 'fa-clock-rotate-left',
    name: 'Assignment history',
    order: 40,
  },
  {
    handle: ARCHIVE_ASSIGNMENT,
    icon: 'fa-eye-slash',
    name: 'Archive assignment',
    extraCheck: (assignment: Assignment) => !assignment.archived,
    order: 50,
  },
  {
    handle: UNARCHIVE_ASSIGNMENT,
    icon: 'fa-eye',
    name: 'Unarchive assignment',
    extraCheck: (assignment: Assignment) => assignment.archived,
    order: 60,
  },
]

export const ItemSettingsConfig: Action[] = [
  {
    handle: RECREATE_LABEL,
    icon: 'fa-rotate',
    name: 'Recreate Item label',
    types: [SINGLE, CONSOLIDATE],
    extraCheck: (assignment: Assignment, isStaff: boolean) => !assignment.archived && isStaff,
    order: 10,
  },
  {
    handle: INTEGRATION_ITEM,
    icon: 'fa-right-to-bracket',
    name: 'Integration logs',
    extraCheck: (assignment: Assignment) => assignment.source == 'integration',
    order: 20,
  },
  {
    handle: ITEM_HISTORY,
    icon: 'fa-clock-rotate-left',
    name: 'Item history',
    order: 30,
  },
]

export const prepareAction = (assignment: ComputedRef<Assignment>, isStaff: boolean) => (action: Action) => {
  let { statuses, name, extraCheck } = action

  if (statuses && typeof statuses === 'function') {
    statuses = statuses(assignment.value, isStaff)
  }

  if (name && typeof name === 'function') {
    name = name(assignment.value, isStaff)
  }

  if (extraCheck && typeof extraCheck === 'function') {
    extraCheck = extraCheck(assignment.value, isStaff)
  }

  return {
    ...action,
    statuses,
    name,
    extraCheck,
  }
}

export const filterAction = (assignment: ComputedRef<Assignment>) => (action: Action) => {
  if (action.extraCheck === false) {
    return false
  }

  if (action.types && !action.types.includes(assignment.value.type)) {
    return false
  }

  if (!action.statuses) {
    return true
  }

  const accountStore = useAccountStore()
  const isStaff = computed(() => accountStore.isStaff)
  const statuses: string[] = Array.isArray(action.statuses)
    ? action.statuses
    : action.statuses(assignment.value, isStaff.value)

  return statuses.includes(assignment.value.status)
}

export const transformAction = (assignment: ComputedRef<Assignment>, isStaff: boolean) => (action: Action) => {
  let { name } = action
  const { handle, type, order, icon } = action

  if (typeof name === 'function') {
    name = name(assignment.value, isStaff)
  }

  return {
    handle,
    name,
    type,
    order,
    icon,
  }
}

export const sortAction = (a: any, b: any) => a.order - b.order

//////// Handlers ////////

export function useActionHandlers(assignment: ComputedRef<Assignment>, currentAction: ComputedRef<Action>) {
  const accountStore: AccountStore = useAccountStore()
  const assignmentStore: AssignmentStore = useAssignmentStore()
  const assignmentSummaryStore: AssignmentSummaryStore = useAssignmentSummaryStore()
  const itemStore: ItemStore = useItemStore()
  const downloadLabelsAction = useDownloadLabelsAction()

  const checkedItem: ComputedRef<string | null> = computed(() => itemStore.checkedItems[0] || null)
  const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)

  const detailsRoute: ComputedRef<RouterLink> = computed(() => ({
    params: { id: assignment.value?.reference || 'undefined' },
    name: isStaff.value ? STAFF_ASSIGNMENTS_DETAILS : CUSTOMER_ASSIGNMENTS_DETAILS,
  }))

  const reportRoute: ComputedRef<RouterLink> = computed(() => ({
    name: isStaff.value ? STAFF_ASSIGNMENTS_OPTIMIZATION_REPORT : CUSTOMER_ASSIGNMENTS_OPTIMIZATION_REPORT,
    params: { id: assignment.value?.reference || 'undefined' },
  }))

  const shipmentsRoute: ComputedRef<RouterLink> = computed(() => ({
    name: isStaff.value ? STAFF_SHIPMENTS : CUSTOMER_SHIPMENTS,
    query: { assignment: assignment.value?.reference || 'undefined' },
  }))

  const labelsDownloaded: ComputedRef<boolean> = computed(() =>
    Boolean(
      [LABELS_RELEASED, LABELS_CREATING, REPORT_RELEASED].includes(assignment.value.status) &&
        assignment.value.labelsDownloadedAt,
    ),
  )

  const actionRoute: ComputedRef<RouterLink | null> = computed(() => resolveRoute(currentAction.value))
  const actionLink: ComputedRef<string | null> = computed(() => resolveLink(currentAction.value))

  function actionFn() {
    callAction(currentAction.value)
  }

  function resolveRoute(action?: Action) {
    if (!action) {
      return null
    }
    switch (action.handle) {
      case VIEW_ASSIGNMENT:
        return detailsRoute.value
      case VIEW_REPORT:
        return reportRoute.value
      case VIEW_SHIPMENTS:
        return shipmentsRoute.value
      default:
        return null
    }
  }

  function resolveLink(action?: Action) {
    if (!action) {
      return null
    }
    switch (action.handle) {
      case DOWNLOAD_REPORT:
        return assignment.value?.optimisationReportUrl || ''
      case DOWNLOAD_DOCUMENTS:
        return assignment.value?.documentsUrl || ''
      default:
        return null
    }
  }

  function callAction(action?: Action) {
    if (!action) {
      return
    }
    switch (action.handle) {
      case DOWNLOAD_SOURCE:
        downloadSource()
        break
      case DOWNLOAD_LABELS:
        downloadLabels()
        break
      case ARCHIVE_ASSIGNMENT:
        archiveAssignment()
        break
      case UNARCHIVE_ASSIGNMENT:
        unArchiveAssignment()
        break
      case PROCESS_ASSIGNMENT:
        processAssignment()
        break
      case STATUS_MANAGEMENT:
        statusManagement()
        break
      case INTEGRATION_ASSIGNMENT:
        integrationAssignment()
        break
      case ASSIGNMENT_HISTORY:
        assignmentHistory()
        break
      case SYNC_TRACKING:
        syncTracking()
        break
      case REOPTIMIZE:
        reoptimize()
        break
      case RECREATE_LABELS:
        recreateLabels()
        break
      case INTEGRATION_ITEM:
        integrationItem()
        break
      case ITEM_HISTORY:
        itemHistory()
        break
      case RECREATE_LABEL:
        recreateLabel()
        break
      default:
        break
    }
  }

  function integrationItem() {
    if (!checkedItem.value) {
      notify({ group: 'error', text: 'Item does not set.' })
      return
    }
    openModal(ITEM_INTEGRATION_MODAL, { itemId: checkedItem.value })
  }

  function itemHistory() {
    if (!checkedItem.value) {
      notify({ group: 'error', text: 'Item does not set.' })
      return
    }
    openModal(ITEM_HISTORY_MODAL, { itemId: checkedItem.value })
  }

  function recreateLabel() {}

  function statusManagement() {
    if (!assignment.value?.reference) {
      notify({ group: 'error', text: 'The assignment does not set.' })
      return
    }
    openModal(ASSIGNMENT_STATUS_MODAL, { assignmentRef: assignment.value.reference })
  }

  function integrationAssignment() {
    if (!assignment.value?.reference) {
      notify({ group: 'error', text: 'The assignment does not set.' })
      return
    }
    openModal(ASSIGNMENT_INTEGRATION_MODAL, { assignmentRef: assignment.value.reference })
  }

  function assignmentHistory() {
    if (!assignment.value?.reference) {
      notify({ group: 'error', text: 'The assignment does not set.' })
      return
    }
    openModal(ASSIGNMENT_HISTORY_MODAL, { assignmentRef: assignment.value.reference })
  }

  function syncTracking() {
    if (!assignment.value?.item?.shipmentNumber) {
      notify({ group: 'error', text: 'The assignment does not have a shipment number.' })
      return
    }
    new TrackingProxy()
      .syncEvents(assignment.value.item.shipmentNumber)
      .then(() => {
        notify({ group: 'success', text: 'Tracking events have been successfully synced.' })
      })
      .catch(catchErrors)
  }

  function reoptimize() {
    new AssignmentProxy()
      .reoptimize(assignment.value.id)
      .then(() => {
        notify({ group: 'success', text: 'Assignment reoptimized.' })
      })
      .catch(catchErrors)
  }

  function recreateLabels() {
    new AssignmentProxy()
      .recreateLabels(assignment.value.id)
      .then(() => {
        notify({ group: 'success', text: 'Labels recreated.' })
      })
      .catch(catchErrors)
  }

  function downloadSource() {
    assignment.value?.sourceFiles.forEach((file: SourceFile) => {
      window.open(file.fileUrl, '_blank')
    })
  }

  function downloadLabels() {
    downloadLabelsAction([assignment.value])
  }

  function archiveAssignment() {
    openModal('confirmWrap', {
      title: 'Are you sure you want to archive the assignment?',
      message: 'The assignment will be forwarded to “Archived assignments" filter section',
      onConfirm: () => {
        new AssignmentStatusProxy(assignment.value?.reference).submitCancel().then(() => {
          assignmentStore.fetch()
        })
      },
    })
  }

  function unArchiveAssignment() {
    openModal('confirmWrap', {
      title: 'Are you sure you want to restore the assignment?',
      message: 'The assignment will be moved back to active assignments',
      onConfirm: () => {
        new AssignmentStatusProxy(assignment.value?.reference).submitUnArchive().then(() => {
          assignmentStore.fetch()
        })
      },
    })
  }

  function processAssignment() {
    openModal('confirmWrap', {
      title: 'Process the assignment',
      message: 'Are you sure you want to process the assignment? This action will start the optimization process.',
      okButtonText: 'Process',
      onConfirm: () => {
        // todo - will not work because we don't have assignment in state in the list action
        assignmentSummaryStore
          .submitProcess()
          .then(() => {
            assignmentStore.fetch()
          })
          .catch((e) => {
            notify({
              group: 'error',
              text: 'The assignment could not be processed. ' + e.message,
            })
          })
      },
    })
  }

  return {
    labelsDownloaded,
    actionRoute,
    actionLink,
    actionFn,
  }
}
